import { Coordinate } from "game-common/models"

export const BUILDING_SHAPES = ["rectangle", "ellipse", "line"] as const
export type BuildingShape = typeof BUILDING_SHAPES[number]

export const buildingShapeCells = (anchor: Coordinate, corner: Coordinate, shape: BuildingShape, hollow: boolean,
    width: number, height: number, limit = 256): Coordinate[] => {
    const result: Coordinate[] = []
    const seen = new Set<string>()
    const add = (x: number, y: number) => {
        const key = `${x},${y}`
        if (result.length >= limit || x < 0 || y < 0 || x >= width || y >= height || seen.has(key)) return
        seen.add(key)
        result.push({ x, y })
    }
    const dx = corner.x - anchor.x, dy = corner.y - anchor.y
    const connect = (from: Coordinate, to: Coordinate) => {
        const dx = to.x - from.x, dy = to.y - from.y
        const nx = Math.abs(dx), ny = Math.abs(dy)
        let x = from.x, y = from.y, ix = 0, iy = 0
        add(x, y)
        while ((ix < nx || iy < ny) && result.length < limit) {
            if (ix < nx && (iy >= ny || (1 + 2 * ix) * ny <= (1 + 2 * iy) * nx)) {
                x += Math.sign(dx); ix++
            } else { y += Math.sign(dy); iy++ }
            add(x, y)
        }
    }
    if (shape === "line") {
        connect(anchor, corner)
        return result
    }
    const minX = Math.max(0, Math.min(anchor.x, corner.x)), maxX = Math.min(width - 1, Math.max(anchor.x, corner.x))
    const minY = Math.max(0, Math.min(anchor.y, corner.y)), maxY = Math.min(height - 1, Math.max(anchor.y, corner.y))
    const cx = (anchor.x + corner.x) / 2, cy = (anchor.y + corner.y) / 2
    const rx = Math.abs(dx) / 2, ry = Math.abs(dy) / 2
    if (shape === "ellipse" && hollow && rx > 0 && ry > 0) {
        const steps = Math.max(16, Math.ceil(Math.max(rx, ry) * 16))
        const pointAt = (step: number) => ({ x: Math.round(cx + rx * Math.cos(step * Math.PI * 2 / steps)),
            y: Math.round(cy + ry * Math.sin(step * Math.PI * 2 / steps)) })
        let previous = pointAt(0)
        for (let step = 1; step <= steps && result.length < limit; step++) {
            const point = pointAt(step)
            connect(previous, point)
            previous = point
        }
        return result
    }
    const inside = (x: number, y: number) => {
        if (x < minX || x > maxX || y < minY || y > maxY) return false
        if (rx === 0 || ry === 0) return true
        return ((x - cx) / (rx + 0.5)) ** 2 + ((y - cy) / (ry + 0.5)) ** 2 <= 1 + 1e-9
    }
    const stepX = dx >= 0 ? 1 : -1, stepY = dy >= 0 ? 1 : -1
    for (let y = stepY > 0 ? minY : maxY; y >= minY && y <= maxY; y += stepY) {
        for (let x = stepX > 0 ? minX : maxX; x >= minX && x <= maxX; x += stepX) {
            if (shape === "ellipse") {
                if (!inside(x, y)) continue
                if (hollow && inside(x - 1, y) && inside(x + 1, y) && inside(x, y - 1) && inside(x, y + 1)) continue
            } else if (hollow && x !== minX && x !== maxX && y !== minY && y !== maxY) continue
            add(x, y)
            if (result.length >= limit) return result
        }
    }
    return result
}
